import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Blobs } from './Blobs';
import { Tilt } from './Tilt';
import { TickerBar } from './TickerBar';
import { HeroProductMock } from './HeroProductMock';
import { useCountUp } from './useCountUp';
import { useMotionSafe } from './motion';

/**
 * PlyEZ hero — CINEMATIC. Serif headline with a single cinnabar-underlined
 * word, dual CTA, three count-up stats, and a 3D mouse-tilt product mock
 * over drifting washi blobs. This is the one section with looping motion;
 * everything is gated through the foundation primitives (Blobs/Tilt) and
 * useMotionSafe(), so reduced-motion users get a calm static hero.
 */
export function HeroSection() {
  const { t } = useTranslation();
  const motionSafe = useMotionSafe();
  const closeHours = useCountUp(8, 1200);

  // Under reduced motion the entrance fades are skipped (render in place).
  const entrance = (delay: number) =>
    motionSafe
      ? {
          initial: { opacity: 0, y: 14 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.55, delay },
        }
      : { initial: false as const };

  return (
    <section data-landing className="relative isolate overflow-hidden">
      <Blobs />
      <TickerBar />

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-16 pb-20 lg:pt-24 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-7">
            <motion.div
              {...entrance(0)}
              className="inline-flex items-center gap-2 rounded-full pill-pearl px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] mb-7"
            >
              <span className="sparkle-dot" aria-hidden />
              {t('landing.heroEyebrow')}
            </motion.div>

            <motion.h1
              {...entrance(0.05)}
              className="display text-[2.6rem] sm:text-5xl md:text-[3.5rem] lg:text-[4.2rem] font-medium tracking-tight leading-[0.98] text-foreground text-balance"
            >
              {t('landing.heroHeadlineTop')}{' '}
              <span className="underline-cinnabar">{t('landing.heroHeadlineHighlight')}</span>
              {t('landing.heroHeadlineTail')}
            </motion.h1>

            <motion.p
              {...entrance(0.2)}
              className="mt-7 max-w-[36rem] text-base md:text-[1.05rem] text-muted-foreground leading-relaxed text-pretty"
            >
              {t('subtag')}
            </motion.p>

            <motion.div
              {...entrance(0.32)}
              className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-3"
            >
              <Link
                to="/signup"
                className="inline-flex h-11 items-center gap-2 px-6 rounded-lg bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cinnabar-500"
              >
                {t('nav.signUp')}
                <ArrowRight size={16} className="rtl-flip" />
              </Link>
              <Link
                to="/demo"
                className="inline-flex h-11 items-center gap-2 px-6 rounded-lg border border-border text-sm font-semibold hover:bg-muted transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cinnabar-500"
              >
                {t('nav.demo')}
              </Link>
              <span className="text-xs text-muted-foreground sm:ml-2">{t('freeForever')}</span>
            </motion.div>

            <motion.div {...entrance(0.42)} className="mt-12 grid grid-cols-3 max-w-md">
              <Stat value="14" label={t('hero.stat_languages')} />
              <Stat
                value={`${Math.round(closeHours.value)}h`}
                label={t('hero.stat_time_to_close')}
                refEl={closeHours.ref}
                bordered
              />
              <Stat value="130+" label={t('hero.stat_currencies')} bordered />
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <Tilt maxDeg={8} className="[transform-style:preserve-3d]">
              <HeroProductMock />
            </Tilt>
          </div>
        </div>
      </div>

      <div className="rule mx-auto max-w-7xl" />
    </section>
  );
}

function Stat({
  value,
  label,
  refEl,
  bordered,
}: {
  value: string;
  label: string;
  refEl?: React.MutableRefObject<HTMLElement | null>;
  bordered?: boolean;
}) {
  return (
    <div className={'flex flex-col py-1 ' + (bordered ? 'border-l border-border/80 pl-5' : 'pr-5')}>
      <span
        ref={refEl as React.RefObject<HTMLSpanElement>}
        className="num text-2xl md:text-3xl font-semibold text-foreground tabular-col"
      >
        {value}
      </span>
      <span className="mt-1 text-[10.5px] uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </span>
    </div>
  );
}
